import { Show, createSignal, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { Progress } from "../components/progress"
import {  ListItem,  Atelier, KeyOfType, IAddress } from "../types";
import { A, useNavigate, useRouteData } from "@solidjs/router";
import { fetchPost, scrollToTop } from "../library/common";
import { TFormValidator, useForm } from "../components/universal/validation";
import toast from "solid-toast"
import { getApplicationStatusJson, loadDetailsCms } from "../app"
import { CMS } from "../components/cms/cms"
import { ModelCheckbox, ModelEditor, ModelProvider, ModelSelect, ModelTextBox } from "../components/model-form-elements";
import { Required } from "../components/universal/required-icon";
import { Checkboxes } from "../components/checkboxes";
import { IQuillEditorWrapper } from "../components/universal/quill-editor";
import { GoogleMap } from "../components/Map";
import { Marker } from "@googlemaps/markerclusterer";
import { useGoogleMapsGeocoder } from "../components/google-maps-geocoder-provider";
import { SectionHeader } from "../components/section-header";
import { PageLoading } from "../components/page-loading";

type State = {
    isCMSReady: boolean;
}

type Resources = {
    Accreditations: ListItem[];
    Currencies: ListItem[];
    Periods: ListItem[];
    Languages: ListItem[];
    Countries: ListItem[];
    Genders: ListItem[];
}
export function Details () {
    const [state, setState] = createStore<State>({ isCMSReady: false } as State);
    const [resources, setResources] = createStore<Resources>({
        Languages: [],
        Periods: [],
        Accreditations: [],
        Currencies: [],
        Countries: [],
        Genders: []
    } as Resources);
    const navigate = useNavigate();
    const required = (el: HTMLInputElement) => !el.value && "Please enter a value";
    const minValue = (min: number, el: HTMLInputElement) => parseInt(el.value) < min && `${el.name || "Field"} must be no less than ${min}`;
    const maxValue = (max: number, el: HTMLInputElement) => parseInt(el.value) > max && `${el.name || "Field"} must be no greater than ${max}`;
    const maxLength = (max: number, el: HTMLInputElement) =>
        el.value?.length > max && `This field must be less than ${max} characters in length`;
    const { formSubmit, validate, errors, setErrors } = useForm({ errorClass: "error-input" }) as TFormValidator<Atelier>;
    const [model, setModel] = createStore<Atelier>({
        Id: "",
        Name: "",
        Subtitle: "",
        CountryId: "",
        ContactEmail: "",
        ContactName: "",
        StreetLine1: "",
        StreetLine2: "",
        AccreditationDescription: "",
        City: "",
        StateName: "",
        PostalCode: "",
        PhoneNumber: "",
        FullTimeTuition: "",
        OffersAccomodation: false,
        Students: "",
        Latitude: "",
        Longitude: "",
        FullTimeTuitionCurrency: "USD",
        FullTimeInstructors: "",
        FullTimeTuitionPeriodId: "",
        PartTimeTuitionPeriodId: "",
        PartTimeInstructors: "",
        PartTimeTuition: "",
        OffersAccommodation: false,
        YearSchoolFounded: "",
        PartTimeTuitionCurrency: "USD",
        ProfileImageUrl: "",
        PhotoId: "",
        Abstract: "",
        Summary: "",
        AccreditationsArray: [],
        CourseLanguagesArray: [],
        WebsiteURL: ""
    } as any);
    const toastError = (err: string) => {
        toast.error(err);
    }
    const isRequired = (field: keyof KeyOfType<Atelier, string | string[] | IQuillEditorWrapper>, message?: string) => {
        let isValid = true;
        if (!model[field]) {
            isValid = false;
        }
        if (model[field] === "null") {
            isValid = false;
        }
        if ((model[field] as string[])?.length === 0) {
            isValid = false;
        }
        if ((model[field] as IQuillEditorWrapper)?.isEmpty) {
            if ((model[field] as IQuillEditorWrapper)?.isEmpty()) { 
                isValid = false;
            }
        }
        if (!isValid) {
            setErrors(field, message || "Please provide a value");
            return false;
        }
        return true;
    }
    const isValid = async () => {
        let isValid = true;
        isValid &&= isRequired("ContactEmail");
        isValid &&= isRequired("ContactName");
        isValid &&= isRequired("City");
        isValid &&= isRequired("StateName");
        isValid &&= isRequired("PhoneNumber");
        isValid &&= isRequired("PostalCode");
        isValid &&= isRequired("CountryId");
        isValid &&= isRequired("CourseLanguagesArray", "You must select at least one language");
        isValid &&= isRequired("SummaryEditor", "Please provide a Full Description");
        isValid &&= isRequired("AbstractEditor", "Please provide a Short Description");
        return isValid;
    }
    const getModel = () => {
        return {
            ...model,
            StreetAddress1: model.StreetAddress1,
            StreetAddress2: model.StreetAddress2,
            CourseLanguages: model.CourseLanguagesArray?.join(","),
            Accreditation: model.AccreditationsArray?.join(","),
            Abstract: model.AbstractEditor?.getHtml(),
            Summary: model.SummaryEditor?.getHtml(),
        };
    };
    const getErrors = (obj: any) => Object.keys(obj).filter(key => obj[key].length);
    const saveProfile = async () => {
        const model = getModel();
        console.log("saveProfile", { model });
        const response = await fetchPost("/AtelierApplication/SaveProfileJson", model);
        const json = await response.json();
        console.log("saveProfile", { response, json });
        if (!json.Success) {
            return {
                Success: false,
                Errors: json.Errors
            };
        }
        return {
            Success: true
        };
    };
    const goToStep2 = () => {
        toast.success("Your application profile has been saved.");
        navigate("/step-2");
    };
    const save = async () => {
        if (!await isValid()){
            return;
        }
        const result = await saveProfile();
        if (!result.Success) {
            return toastError("[005] Please check the form for errors: " + getErrors(result.Errors).join("; "));
        }
        await getApplicationStatusJson({ forceRefresh: true });
        goToStep2();
    };
    const [mapdata, setMapData] = createSignal<{ markers: any[]; heatmap: any[] }>({ markers: [], heatmap: [] });
    const handleSubmit = async () => {
        await save();
    }; 
    onMount(async () => {
        const routeData = await useRouteData() as any;
        await loadDetailsCms();
        setResources(routeData.resources);
        setModel(routeData.step1);
        setModel("CourseLanguagesArray", routeData.step1.CourseLanguages?.split(",") || []);
        setModel("AccreditationsArray", routeData.step1.Accreditation?.split(",") || []);
        const semester = resources.Periods.find(x => x.Text == "Semester");
        if (semester) {
            !model.FullTimeTuitionPeriodId && setModel("FullTimeTuitionPeriodId", semester.Value);
            !model.PartTimeTuitionPeriodId && setModel("PartTimeTuitionPeriodId", semester.Value);
        }
        setState({ isCMSReady: true });
        scrollToTop();
    });
    const updateList = (value: string, field: keyof KeyOfType<Atelier, string[]>) => {
        const i = model[field].findIndex(x => x == value);
        if (i < 0) {
            setModel(field, [...model[field], value]);
        } else {
            setModel(field, model[field].filter(x => x != value));
        }
    }
    const accreditationChecked = (value: string) => updateList(value, "AccreditationsArray");
    const courseLanguageChecked = (value: string) => updateList(value, "CourseLanguagesArray");
    const updateAddress = async (address: IAddress) => {
        setModel(address);
    };
    const updateFromMarker = async (address: IAddress) => {
        setModel(address);
    };
    const onLookupAddressClicked = async (e: Event) => {
        e.preventDefault();
        try {
            const results = await geocoder.lookupAddress("model.location");
            console.log({ results })
            const location = results[0];
            const marker = {
                latitude: location.geometry.location.lat(),
                longitude: location.geometry.location.lng(),    
            };
            setMapData({ markers: [marker], heatmap: [] });
        } catch (error) {
            console.error("Error in main:", error);
        }
    };
    function onMarkerClicked(marker: Marker, map: google.maps.Map): void {
        console.log("Marker clicked", { marker, map });
    }
    return (
        <>
            <SectionHeader>
                <h1>
                    Profile
                </h1>
                <p>
                    <a href="/"><CMS text id="nav::home">Home</CMS></a> / <A href="/introduction"><CMS text id="nav::atelier-application">ARC Atelier Application</CMS></A>
                </p>
            </SectionHeader>
            <Show when={state.isCMSReady} fallback={<PageLoading />}>
                <ModelProvider model={model} setModel={setModel} validation={{ errors, validate }}>
                    <div style="font-family: 'Bariol-Regular'; font-size: 1.25em;">
                        <Progress step={1} />
                        <h4 class="salon-heading-md" style="text-align: center;">
                            ARC Atelier Application
                        </h4>
                        <form use:formSubmit={handleSubmit}>
                            <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                                <CMS text id="label::basic-details">Basic details</CMS>
                            </h4>
                            <CMS id="24th-scholarship-application/step-1::your-details/description"></CMS>
                            <hr />
                            <div class="row m-t-lg">
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier> field="Name" required />
                                </div>
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier> field="Subtitle" label={() => <>Subtitle <small>(Optional)</small></>} />
                                </div>
                            </div>
                            <div class="row m-t-lg">
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier> field="WebsiteURL" label="URL" required />
                                </div>
                                <div class="col-md-2">
                                    <ModelTextBox<Atelier> field="YearSchoolFounded" label="Year school was founded" />
                                </div>
                            </div>

                            <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                                Courses, accreditation, and finance
                            </h4>
                            <hr />
                            <div class="row m-t-lg">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> Course languages (Check multiple if applicable)
                                    </label>
                                    <Checkboxes
                                        values={() => model.CourseLanguagesArray}
                                        list={resources.Languages}
                                        name="CourseLanguagesArray"
                                        onInput={courseLanguageChecked}
                                    />
                                </div>
                                <div class="col-md-6">
                                    <label>
                                        Accreditations (Check multiple if applicable)
                                    </label>
                                    <Checkboxes
                                        values={() => model.AccreditationsArray}
                                        list={resources.Accreditations}
                                        name="AccreditationsArray"
                                        onInput={accreditationChecked}
                                    />
                                    <Show when={model.AccreditationsArray.some(x => x === "Other")}>
                                        <div>
                                            <ModelTextBox<Atelier>
                                                field="AccreditationDescription"
                                                required
                                                label={() => <><CMS id="label::name-of-the-accreditation">Name of the accreditation</CMS></>}
                                            />
                                        </div>
                                    </Show>
                                </div>
                            </div>
                            <hr />
                            <div class="divider"></div>
                            <p>
                                <CMS id="heading::representative-figures">
                                    Enter representative figures at the time of completing this application
                                </CMS>
                            </p>
                            <div class="row m-t-lg">
                                <div class="col-md-4">
                                    <ModelTextBox<Atelier> field="Students" label="Number of students" width="33%" required />
                                </div>
                                <div class="col-md-4">
                                    <ModelTextBox<Atelier> field="FullTimeInstructors" width="33%" required label="Number of full-time instructors" />
                                </div>
                                <div class="col-md-4">
                                    <ModelTextBox<Atelier> field="PartTimeInstructors" width="33%" label="Number of part-time instructors" />
                                </div>
                            </div>
                            <hr/>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="row">
                                        <div class="col-md-8">
                                            <label>
                                                <Required /> <CMS id="label::full-time-tuition-costs">Full time tuition costs <small>e.g., 1234.56</small></CMS>
                                            </label>
                                            <div class="input-group">
                                                <ModelTextBox<Atelier> field="FullTimeTuition" width="80%" nolabel required />
                                                <ModelSelect<Atelier> field="FullTimeTuitionCurrency" list={resources.Currencies} nolabel width="20%" />
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="input-group">
                                                <ModelSelect<Atelier> field="FullTimeTuitionPeriodId" list={resources.Periods} label="Period" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="row">
                                        <div class="col-md-8">
                                            <label>
                                                <CMS id="label::part-time-tuition-costs">Part time tuition costs <small>e.g., 1234.56</small></CMS>
                                            </label>
                                            <div class="input-group">
                                                <ModelTextBox<Atelier> field="PartTimeTuition" width="80%" nolabel />
                                                <ModelSelect<Atelier> field="PartTimeTuitionCurrency" list={resources.Currencies} width="20%" nolabel />
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <ModelSelect<Atelier> field="PartTimeTuitionPeriodId" list={resources.Periods} label="Period" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                                Accommodation
                            </h4>
                            <hr />
                            <div class="row">
                                <div class="col-md-6">
                                    <ModelCheckbox<Atelier>
                                        field="OffersAccommodation"
                                        label={() => <CMS text id="label::we-offer-housing">We offer housing or can arrange it</CMS>}
                                    />
                                </div>
                            </div>

                            <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                                Contact information
                            </h4>
                            <hr />
                            <div class="row">
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier>
                                        field="ContactName"
                                        required
                                        label={() => <CMS id="label::atelier-contact-person">Contact <small>Name of your contact person</small></CMS>}
                                    />
                                </div>
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier>
                                        field="ContactEmail"
                                        type="email"
                                        required
                                        label={() => <CMS id="label::atelier-contact-email">Email <small>Your contact person's email</small></CMS>}
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <ModelTextBox<Atelier>
                                        field="PhoneNumber"
                                        required
                                        label={() => <CMS id="label::atelier-contact-phone-number">Phone <small>The school's contact phone number</small></CMS>}
                                    />
                                </div>
                            </div>

                            <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                                Address
                            </h4>
                            <hr />
                            <div>
                                <div class="col-md-8">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <ModelTextBox<Atelier> field="StreetAddress1" label="Street (line 1)" required />
                                        </div>
                                        <div class="col-md-6">
                                            <ModelTextBox<Atelier> field="StreetAddress2" label="Street (line 2)" />
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-md-3">
                                            <ModelTextBox<Atelier> field="City" label="City" required />
                                        </div>
                                        <div class="col-md-3">
                                            <ModelTextBox<Atelier> field="StateName" label="State/Province" required />
                                        </div>
                                        <div class="col-md-3">
                                            <ModelTextBox<Atelier> field="PostalCode" label="Post code (ZIP)" required />
                                        </div>
                                        <div class="col-md-3">
                                            <ModelSelect<Atelier> field="CountryId" list={resources.Countries} label="Country" required />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    {/* <button class="btn btn-primary" type="button" onClick={onLookupAddressClicked}>
                                            Show on the map
                                    </button>
                                    <GoogleMap
                                        data={mapdata}
                                        mode={() => "markers"}
                                        width="100%"
                                        height="300px"
                                        onMarkerClicked={onMarkerClicked}
                                    /> */}
                                </div>
                            </div>

                            <div style="position: relative;margin-top: 60px;">
                                <div class="row">
                                    <div class="col-md-12">
                                        <ModelEditor<Atelier>
                                            field="Abstract"
                                            editor="AbstractEditor"
                                            required
                                            label={() => <CMS text id="label::atelier-short-description">Short description (500 characters or less)</CMS>}
                                        />
                                    </div>
                                </div>
                                <div class="row" style="margin-top:20px;">
                                    <div class="col-md-12">
                                        <ModelEditor<Atelier>
                                            field="Summary"
                                            editor="SummaryEditor"
                                            required
                                            label={() => <CMS text id="label::atelier-full-description">Full description</CMS>}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="row m-t-xxl m-b-lg">
                                <div class="col-md-12">
                                    <div id="submit-row">
                                        <A href="/introduction" class="btn btn-chunky-lg btn-chunky-dark-grey">
                                            <CMS id="button::back" text>Back</CMS>
                                        </A>
                                        <button type="submit" class="btn btn-chunky-lg btn-chunky-goldenrod">
                                            <CMS text id="button::next">Next</CMS>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </ModelProvider>
            </Show>
        </>
      );
};